// packages/web-ui/src/AppShell/TabBar.tsx
//
// W2-S4 AC #1, #4, #5, #6, #7, #12 — the `role="tablist"` rendered above
// `<TabContent>` inside `<TabContentArea>`. Wraps the open tab list in
// dnd-kit's `<DndContext>` + `<SortableContext>` (sortable preset). Clicks
// activate; middle-click closes; right-click opens the context menu;
// dirty-tab close routes through `<CloseConfirmDialog>`.
//
// Source of truth: ACs of story 2-4 + UX §2.2.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX, MouseEvent } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X } from 'lucide-react';
import { useRootStore } from '../state/store.js';
import { selectOpenTabs, type TabState } from '../state/navigation.js';
import type { RootStore } from '../state/store.js';
import { TabBarItem } from './TabBar/TabBarItem.js';
import { TabContextMenu } from './TabBar/TabContextMenu.js';
import { CloseConfirmDialog } from './TabBar/CloseConfirmDialog.js';
import { useReducedMotion } from './useReducedMotion.js';
import { useTabBarKeyboardNav } from './useTabBarKeyboardNav.js';
import { Z_INDEX } from './responsive-tokens.js';
import { DeleteConfirmDialog } from '../components/SessionList/DeleteConfirmDialog.js';
import { sessionsStoreSingleton } from '../state/sessions-store-singleton.js';
import { closeDeletedSessionTabs } from '../state/close-session-tabs.js';

interface ContextMenuState {
  tabId: string;
  x: number;
  y: number;
}

/**
 * Sortable wrapper around `<TabBarItem>`. Each invocation wires `useSortable`
 * for one tab, applies the transform, and forwards drag handlers to the item.
 */
function SortableTabItem(props: {
  tab: TabState;
  isActive: boolean;
  isFocused: boolean;
  focusIndex: number;
  isDraggingPinned: boolean;
  isDraggingUnpinned: boolean;
  onActivate: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onContextMenu: (event: MouseEvent<HTMLElement>, tabId: string) => void;
  registerRef: (tabId: string, el: HTMLButtonElement | null) => void;
}): JSX.Element {
  const {
    tab,
    isActive,
    isFocused,
    focusIndex,
    isDraggingPinned,
    isDraggingUnpinned,
    onActivate,
    onClose,
    onContextMenu,
    registerRef,
  } = props;

  // Pinned-first invariant: while dragging a pinned tab, lock unpinned tabs;
  // while dragging an unpinned tab, lock pinned tabs.
  const disabled =
    (isDraggingPinned && !tab.pinned) || (isDraggingUnpinned && tab.pinned);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <TabBarItem
      tab={tab}
      isActive={isActive}
      isFocused={isFocused}
      focusIndex={focusIndex}
      onActivate={onActivate}
      onClose={onClose}
      onContextMenu={onContextMenu}
      ref={(el) => {
        setNodeRef(el);
        registerRef(tab.id, el);
      }}
      dragHandleProps={{
        style,
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as unknown as Record<string, (event: unknown) => void>,
      }}
    />
  );
}

/**
 * For chat-session tabs, replace the raw session id with the session's
 * actual title — falling back to "New conversation" if the title is empty
 * or the session meta hasn't loaded yet. Keeps every other tab kind as-is
 * (files already shows the basename, settings/skills already use a real
 * name).
 */
function selectOpenTabsWithLabels(state: RootStore): TabState[] {
  const open = selectOpenTabs(state);
  return open.map((tab) => {
    if (tab.destination === 'chat' && tab.resourceId !== null) {
      const meta = state.sessions.find((m) => m.id === tab.resourceId);
      const title = meta?.title?.trim() ?? '';
      const label = title.length > 0 ? title : 'New conversation';
      return label === tab.label ? tab : { ...tab, label };
    }
    return tab;
  });
}

export function TabBar(): JSX.Element {
  const tabs = useRootStore(selectOpenTabsWithLabels);
  const activeTabId = useRootStore((s) => s.activeTabId);
  const setActiveTab = useRootStore((s) => s.setActiveTab);
  const reorderTabs = useRootStore((s) => s.reorderTabs);
  const closeTab = useRootStore((s) => s.closeTab);
  const closeOthers = useRootStore((s) => s.closeOthers);
  const closeAllToRight = useRootStore((s) => s.closeAllToRight);
  const closeAll = useRootStore((s) => s.closeAll);
  const pinTab = useRootStore((s) => s.pinTab);
  const unpinTab = useRootStore((s) => s.unpinTab);
  const setPendingClose = useRootStore((s) => s.setPendingClose);

  const reducedMotion = useReducedMotion();

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  // Closing a chat *session* tab deletes the underlying conversation (with a
  // confirm), so it can't be re-surfaced by the /chat auto-selector. This
  // pending state drives that confirm dialog; it is separate from the
  // dirty-buffer close-confirm used for file tabs.
  const [pendingChatDelete, setPendingChatDelete] = useState<{
    sessionId: string;
    title: string;
  } | null>(null);
  const [chatDeleting, setChatDeleting] = useState(false);
  const [pendingTargets, setPendingTargets] = useState<TabState[] | null>(null);
  const [pendingAction, setPendingAction] = useState<'close' | 'closeOthers' | 'closeAllToRight' | 'closeAll' | null>(null);
  const [pendingActionPayload, setPendingActionPayload] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const registerRef = useCallback((tabId: string, el: HTMLButtonElement | null) => {
    if (el === null) {
      itemRefs.current.delete(tabId);
    } else {
      itemRefs.current.set(tabId, el);
    }
  }, []);

  // Imperatively focus the tab matching `focusedIndex`. The keyboard nav hook
  // updates `focusedIndex` only — we move the DOM focus here in an effect.
  useEffect(() => {
    if (focusedIndex < 0 || focusedIndex >= tabs.length) return;
    const tab = tabs[focusedIndex];
    if (tab === undefined) return;
    const el = itemRefs.current.get(tab.id);
    el?.focus();
  }, [focusedIndex, tabs]);

  const draggingTab = useMemo(
    () => (draggingId === null ? null : tabs.find((t) => t.id === draggingId) ?? null),
    [draggingId, tabs],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingId(null);
      const { active, over } = event;
      if (over === null || active.id === over.id) return;
      reorderTabs(String(active.id), String(over.id));
    },
    [reorderTabs],
  );

  const handleDragCancel = useCallback(() => {
    setDraggingId(null);
  }, []);

  // ── Closing flow with dirty-confirm ────────────────────────────────────
  const closeOrConfirm = useCallback(
    (tabId: string) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (tab === undefined) return;
      // Chat *session* tabs: closing the tab hard-deletes the conversation
      // (behind a confirm) so it can't reappear via the /chat auto-selector.
      // The bare `chat` placeholder (no resourceId) has nothing to delete and
      // just closes.
      if (tab.destination === 'chat' && tab.resourceId !== null) {
        setPendingChatDelete({ sessionId: tab.resourceId, title: tab.label });
        return;
      }
      if (tab.isDirty) {
        setPendingTargets([tab]);
        setPendingAction('close');
        setPendingActionPayload(tabId);
        setPendingClose([tabId]);
        return;
      }
      closeTab(tabId);
    },
    [closeTab, setPendingClose, tabs],
  );

  // Confirm deleting the conversation behind a chat tab: hard-delete the
  // session, then close its tab (and reconcile the active tab / hash) via the
  // shared `closeDeletedSessionTabs` helper the SessionList delete path uses.
  const handleConfirmChatDelete = useCallback(async () => {
    if (pendingChatDelete === null) return;
    const { sessionId } = pendingChatDelete;
    setChatDeleting(true);
    const ok = await sessionsStoreSingleton.getState().deleteSession(sessionId);
    setChatDeleting(false);
    // Only close the tab once the session is actually gone from disk — on
    // failure we leave the tab open (the sessions slice records `lastError`)
    // so a still-existing conversation can't silently vanish from the bar.
    if (ok) closeDeletedSessionTabs([sessionId]);
    setPendingChatDelete(null);
  }, [pendingChatDelete]);

  const handleCancelChatDelete = useCallback(() => {
    setPendingChatDelete(null);
  }, []);

  const closeOthersOrConfirm = useCallback(
    (tabId: string) => {
      const dirtySet = tabs.filter((t) => !t.pinned && t.id !== tabId && t.isDirty);
      if (dirtySet.length > 0) {
        setPendingTargets(dirtySet);
        setPendingAction('closeOthers');
        setPendingActionPayload(tabId);
        setPendingClose(dirtySet.map((t) => t.id));
        return;
      }
      closeOthers(tabId);
    },
    [closeOthers, setPendingClose, tabs],
  );

  const closeAllToRightOrConfirm = useCallback(
    (tabId: string) => {
      const idx = tabs.findIndex((t) => t.id === tabId);
      const targets = idx === -1 ? [] : tabs.slice(idx + 1).filter((t) => !t.pinned && t.isDirty);
      if (targets.length > 0) {
        setPendingTargets(targets);
        setPendingAction('closeAllToRight');
        setPendingActionPayload(tabId);
        setPendingClose(targets.map((t) => t.id));
        return;
      }
      closeAllToRight(tabId);
    },
    [closeAllToRight, setPendingClose, tabs],
  );

  const closeAllOrConfirm = useCallback(() => {
    const targets = tabs.filter((t) => !t.pinned && t.isDirty);
    if (targets.length > 0) {
      setPendingTargets(targets);
      setPendingAction('closeAll');
      setPendingActionPayload(null);
      setPendingClose(targets.map((t) => t.id));
      return;
    }
    closeAll();
  }, [closeAll, setPendingClose, tabs]);

  const handleConfirmAction = useCallback(
    (action: 'save' | 'discard') => {
      if (action === 'save') {
        // TODO(W3-S4): wire saveAndClose to file save handler — for non-files
        // tabs treat as no-op save then close. For files-tabs the save logic
        // lives in W3-S4's CM6 file viewer; until then this falls through to
        // a discard close.
        if (pendingTargets !== null) {
          for (const t of pendingTargets) {
            if (t.destination === 'files') {
              console.warn('[tessa-lean/web-ui] TODO(W3-S4): wire saveAndClose');
            }
          }
        }
      }
      // Apply the queued bulk-close action.
      switch (pendingAction) {
        case 'close':
          if (pendingActionPayload !== null) closeTab(pendingActionPayload);
          break;
        case 'closeOthers':
          if (pendingActionPayload !== null) closeOthers(pendingActionPayload);
          break;
        case 'closeAllToRight':
          if (pendingActionPayload !== null) closeAllToRight(pendingActionPayload);
          break;
        case 'closeAll':
          closeAll();
          break;
        default:
          break;
      }
      setPendingTargets(null);
      setPendingAction(null);
      setPendingActionPayload(null);
      setPendingClose(null);
    },
    [closeAll, closeAllToRight, closeOthers, closeTab, pendingAction, pendingActionPayload, pendingTargets, setPendingClose],
  );

  const handleCancelClose = useCallback(() => {
    setPendingTargets(null);
    setPendingAction(null);
    setPendingActionPayload(null);
    setPendingClose(null);
  }, [setPendingClose]);

  // ── Context menu trigger ──────────────────────────────────────────────
  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>, tabId: string) => {
      event.preventDefault();
      setContextMenu({ tabId, x: event.clientX, y: event.clientY });
    },
    [],
  );

  // ── Keyboard nav handler ──────────────────────────────────────────────
  const onKeyboardActivate = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
    },
    [setActiveTab],
  );
  const handlers = useMemo(
    () => ({ onActivate: onKeyboardActivate, onClose: closeOrConfirm }),
    [closeOrConfirm, onKeyboardActivate],
  );
  const onTablistKeyDown = useTabBarKeyboardNav(tabs, focusedIndex, setFocusedIndex, handlers);

  // Empty state: render the placeholder (W2-S10 will replace with EmptyWelcome).
  if (tabs.length === 0) {
    return (
      <div
        role="tablist"
        aria-label="Open tabs"
        aria-orientation="horizontal"
        data-testid="tab-bar"
        data-empty="true"
        className="flex"
        style={{ height: 0 }}
      >
        {/* 0 px tall — the empty welcome lives in `<TabContent>`. */}
      </div>
    );
  }

  const activeContextTab =
    contextMenu === null ? null : tabs.find((t) => t.id === contextMenu.tabId) ?? null;

  // The "Close all" toolbar button closes every non-pinned tab (matching the
  // context-menu "Close all" + the `closeAll()` slice action). Hidden when no
  // closeable tab exists (e.g. only pinned tabs remain).
  const closeableCount = tabs.filter((t) => !t.pinned).length;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="border-taupe-300 bg-cream-50 flex items-stretch border-b">
        <SortableContext
          items={tabs.map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div
            role="tablist"
            aria-label="Open tabs"
            aria-orientation="horizontal"
            data-testid="tab-bar"
            className="flex flex-1 overflow-x-auto"
            onKeyDown={onTablistKeyDown}
          >
            {tabs.map((tab, idx) => {
              const isFocused = focusedIndex === idx;
              return (
                <SortableTabItem
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  isFocused={isFocused}
                  focusIndex={idx}
                  isDraggingPinned={draggingTab?.pinned === true}
                  isDraggingUnpinned={draggingTab !== null && !draggingTab.pinned}
                  onActivate={setActiveTab}
                  onClose={closeOrConfirm}
                  onContextMenu={handleContextMenu}
                  registerRef={registerRef}
                />
              );
            })}
          </div>
        </SortableContext>
        {closeableCount > 0 ? (
          <button
            type="button"
            data-testid="tab-bar-close-all"
            aria-label="Close all tabs"
            title="Close all tabs"
            onClick={closeAllOrConfirm}
            className="text-taupe-700 hover:bg-cream-200 hover:text-audi-red border-taupe-300 flex shrink-0 items-center gap-1 border-l px-2 text-xs"
          >
            <X size={14} aria-hidden="true" />
            <span>Close all</span>
          </button>
        ) : null}
      </div>
      <DragOverlay
        data-testid="drag-overlay"
        style={{ zIndex: Z_INDEX.dragOverlay, boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}
        dropAnimation={reducedMotion ? null : undefined}
      >
        {draggingTab !== null ? (
          <div data-testid="drag-overlay-inner" style={{ zIndex: Z_INDEX.dragOverlay }}>
            <TabBarItem
              tab={draggingTab}
              isActive={draggingTab.id === activeTabId}
              isFocused={false}
              focusIndex={-1}
              onActivate={() => undefined}
              onClose={() => undefined}
            />
          </div>
        ) : null}
      </DragOverlay>
      {contextMenu !== null && activeContextTab !== null ? (
        <TabContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          pinned={activeContextTab.pinned}
          onPinToggle={() => {
            if (activeContextTab.pinned) {
              unpinTab(activeContextTab.id);
            } else {
              pinTab(activeContextTab.id);
            }
          }}
          onClose={() => closeOrConfirm(activeContextTab.id)}
          onCloseOthers={() => closeOthersOrConfirm(activeContextTab.id)}
          onCloseAllToRight={() => closeAllToRightOrConfirm(activeContextTab.id)}
          onCloseAll={() => closeAllOrConfirm()}
          onDismiss={() => setContextMenu(null)}
        />
      ) : null}
      <CloseConfirmDialog
        open={pendingTargets !== null}
        targets={pendingTargets ?? []}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelClose}
      />
      <DeleteConfirmDialog
        open={pendingChatDelete !== null}
        sessionTitle={pendingChatDelete?.title ?? ''}
        isDeleting={chatDeleting}
        onCancel={handleCancelChatDelete}
        onConfirm={() => {
          void handleConfirmChatDelete();
        }}
      />
    </DndContext>
  );
}

// re-export `arrayMove` so tests asserting "uses dnd-kit reorder" can spy.
export { arrayMove };
